import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Button, MenuItem, Divider
} from '@mui/material';
import { ArrowBack, Add, Edit, Delete, School, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const API = process.env.REACT_APP_API_URL;

const LEVELS = [
  'Pre-Primary',
  'Lower Primary',
  'Upper Primary',
  'Junior Secondary',
  'Senior Secondary',
  'Foundation'
];

const LEVEL_COLOR = {
  'Pre-Primary':       '#7b1fa2',
  'Lower Primary':     '#1565c0',
  'Upper Primary':     '#2e7d32',
  'Junior Secondary':  '#e65100',
  'Senior Secondary':  '#c62828',
  'Foundation':        '#37474f'
};

const CURRICULA = ['CBC', '8-4-4'];
const CURRICULUM_COLOR = { 'CBC': '#00838f', '8-4-4': '#6a1b9a' };

const emptyForm = { code: '', name: '', level: 'Lower Primary', order: '', minLearningAreas: 0, curriculum: 'CBC', description: '' };

const InfoRow = ({ label, value }) => (
  <Box display="flex" gap={1} mb={0.5}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 500 }}>{label}</Typography>
    <Typography variant="body2">{value || <em style={{ color: '#bbb' }}>—</em>}</Typography>
  </Box>
);

const StageList = () => {
  const navigate = useNavigate();
  const [stages,   setStages]   = useState([]);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');
  const [loading,  setLoading]  = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [form,     setForm]     = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: dialogOpen, value: form, onClose: () => setDialogOpen(false) });
  const [saving,   setSaving]   = useState(false);
  const [viewStage, setViewStage] = useState(null);
  const [confirmDlg, setConfirmDlg] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchStages = async () => {
    try {
      const { data } = await axios.get(`${API}/api/stages`, { headers });
      setStages(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load stages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStages(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 2500);
    return () => clearTimeout(t);
  }, [success]);

  const handleCreate = () => {
    setEditingCode(null);
    const nextOrder = stages.length ? Math.max(...stages.map(s => s.order)) + 1 : 1;
    setForm({ ...emptyForm, order: nextOrder });
    setDialogOpen(true);
  };

  const handleEdit = (s) => {
    setEditingCode(s.code);
    setForm({ code: s.code, name: s.name, level: s.level, order: s.order, minLearningAreas: s.minLearningAreas ?? 0, curriculum: s.curriculum || 'CBC', description: s.description || '' });
    setDialogOpen(true);
  };

  const handleDelete = (code) => setConfirmDlg(code);

  const confirmDelete = async () => {
    const code = confirmDlg;
    setConfirmDlg(null);
    try {
      await axios.delete(`${API}/api/stages/${code}`, { headers });
      setSuccess(`Stage ${code} deleted`);
      fetchStages();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  const handleSave = async () => {
    if (!form.code.trim()) { setError('Stage Code is required'); return; }
    if (!form.name.trim()) { setError('Stage Name is required'); return; }
    if (!form.order)       { setError('Sequence order is required'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, order: parseInt(form.order, 10), minLearningAreas: parseInt(form.minLearningAreas, 10) || 0 };
      if (editingCode) {
        await axios.put(`${API}/api/stages/${editingCode}`, payload, { headers });
        setSuccess(`Stage ${editingCode} updated`);
      } else {
        await axios.post(`${API}/api/stages`, payload, { headers });
        setSuccess(`Stage ${form.code} created`);
      }
      setDialogOpen(false);
      fetchStages();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading...</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <School color="primary" />
              <Box>
                <Typography variant="h5" color="primary">Stages / Grades</Typography>
                <Typography variant="body2" color="text.secondary">
                  Grade levels ordered by sequence — used for learner promotion
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1}>
              <IconButton onClick={() => navigate('/academics')} title="Back to Academics">
                <ArrowBack />
              </IconButton>
              <IconButton onClick={handleCreate} title="New Stage"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Add />
              </IconButton>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          {stages.length === 0 ? (
            <Typography color="text.secondary">No stages yet. Click + to add the first grade level.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Seq</strong></TableCell>
                  <TableCell><strong>Code</strong></TableCell>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Level</strong></TableCell>
                  <TableCell><strong>Curriculum</strong></TableCell>
                  <TableCell align="center"><strong>Min. Areas</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stages.map(s => (
                  <TableRow key={s.code} hover sx={{ cursor: 'pointer' }}
                    onClick={() => setViewStage(s)}>
                    <TableCell>
                      <Chip label={s.order} size="small" variant="outlined" sx={{ fontWeight: 700 }} />
                    </TableCell>
                    <TableCell><strong>{s.code}</strong></TableCell>
                    <TableCell>{s.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={s.level}
                        size="small" variant="outlined"
                        sx={{ color: LEVEL_COLOR[s.level] || '#333', borderColor: LEVEL_COLOR[s.level] || '#ccc', fontSize: 11 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={s.curriculum || 'CBC'}
                        size="small"
                        sx={{ bgcolor: (CURRICULUM_COLOR[s.curriculum] || CURRICULUM_COLOR.CBC) + '18',
                              color: CURRICULUM_COLOR[s.curriculum] || CURRICULUM_COLOR.CBC,
                              fontWeight: 700, fontSize: 11 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {s.minLearningAreas
                        ? <Chip label={s.minLearningAreas} size="small" color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
                        : <Typography variant="caption" color="text.secondary">—</Typography>}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{s.description}</Typography>
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <IconButton size="small" color="primary" onClick={() => handleEdit(s)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(s.code)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── View card ── */}
      <Dialog open={Boolean(viewStage)} onClose={() => setViewStage(null)} maxWidth="xs" fullWidth>
        {viewStage && (
          <>
            <DialogTitle component="div">
              <Box display="flex" alignItems="center" gap={1}>
                <School color="primary" />
                <Box>
                  <Typography variant="h6" lineHeight={1}>{viewStage.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {viewStage.code} · Sequence {viewStage.order}
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <InfoRow label="Code"        value={viewStage.code} />
              <InfoRow label="Name"        value={viewStage.name} />
              <InfoRow label="Level"       value={viewStage.level} />
              <InfoRow label="Curriculum"  value={viewStage.curriculum || 'CBC'} />
              <InfoRow label="Sequence"    value={viewStage.order} />
              <InfoRow label="Min. Learning Areas" value={viewStage.minLearningAreas || '—'} />
              <Divider sx={{ my: 1 }} />
              <InfoRow label="Description" value={viewStage.description} />
            </DialogContent>
            <DialogActions>
              <Button startIcon={<Edit />} onClick={() => { setViewStage(null); handleEdit(viewStage); }}>Edit</Button>
              <Button variant="outlined" onClick={() => setViewStage(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Confirm Delete dialog ── */}
      <Dialog open={Boolean(confirmDlg)} onClose={() => setConfirmDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>Delete stage <strong>{confirmDlg}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── Create / Edit dialog ── */}
      {discardDialog}
      <Dialog open={dialogOpen} onClose={attemptClose} maxWidth="xs" fullWidth>
        <DialogTitle>{editingCode ? `Edit Stage — ${editingCode}` : 'New Stage / Grade'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Stage Code *" value={form.code}
              onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
              disabled={!!editingCode}
              size="small" fullWidth placeholder="e.g. G1, PP1"
              helperText={editingCode ? 'Code cannot be changed' : ''}
            />
            <TextField
              label="Stage Name *" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              size="small" fullWidth placeholder="e.g. Grade 1"
            />
            <TextField
              select label="Level *" value={form.level}
              onChange={e => setForm(f => ({ ...f, level: e.target.value }))}
              size="small" fullWidth>
              {LEVELS.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
            </TextField>
            <TextField
              select label="Curriculum *" value={form.curriculum}
              onChange={e => setForm(f => ({ ...f, curriculum: e.target.value }))}
              size="small" fullWidth
              helperText="CBC → EE/ME/AE/BE levels · 8-4-4 → A…E letter grades. Drives grading & reports for this stage.">
              {CURRICULA.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField
              label="Sequence / Order *" value={form.order}
              onChange={e => setForm(f => ({ ...f, order: e.target.value }))}
              type="number" inputProps={{ min: 1 }}
              size="small" fullWidth
              helperText="Promotion moves learner to next sequence number"
            />
            <TextField
              label="Minimum Learning Areas" value={form.minLearningAreas}
              onChange={e => setForm(f => ({ ...f, minLearningAreas: e.target.value }))}
              type="number" inputProps={{ min: 0 }}
              size="small" fullWidth
              helperText="Least subjects a learner must register for at this grade (0 = no minimum). Term registration is blocked below this."
            />
            <TextField
              label="Description" value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              size="small" fullWidth multiline rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StageList;
